import { useState, useEffect } from 'react'
import { isAdminRole } from '@/utils/roles'
import { motion } from 'framer-motion'
import { useAuth } from '@/stores/auth'
import { useUI } from '@/stores/ui'
import AppHeader from '@/components/common/AppHeader'
import { usersApi, type UserResponse } from '@/api/endpoints/users'
import { parishesApi, type ParishResponse } from '@/api/endpoints/parishes'
import { subscriptionsApi, type SubscriptionStatusResponse } from '@/api/endpoints/subscriptions'
import { parentsApi } from '@/api/endpoints/parents'
import { authApi } from '@/api/endpoints/auth'
import Spinner from '@/components/common/Spinner'
import { SUBSCRIPTION_CONFIG } from '@/config/subscription'
import AppFooter from '@/components/common/AppFooter'
import SubscriptionManagementModal from '@/components/dashboard/modals/SubscriptionManagementModal'
import ParishSetupModal from '@/components/parish/ParishSetupModal'

const ROLE_OPTIONS = ['Priest', 'Deacon', 'Pastor', 'Parish Staff', 'Parish Volunteer', 'Other'] as const

export default function SettingsPage() {
  const { user, updateUser, logout } = useAuth()
  const addToast = useUI((state) => state.addToast)
  
  // Profile fields
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [role, setRole] = useState<string>('')
  const [title, setTitle] = useState('')
  const [cellPhone, setCellPhone] = useState('')
  const [address, setAddress] = useState('')
  const [city, setCity] = useState('')
  const [state, setState] = useState('')
  const [zipCode, setZipCode] = useState('')
  
  // Parish fields
  const [userDetails, setUserDetails] = useState<UserResponse | null>(null)
  const [parishName, setParishName] = useState('')
  const [parishAddress, setParishAddress] = useState('')
  const [parishCity, setParishCity] = useState('')
  const [parishState, setParishState] = useState('')
  const [parishZip, setParishZip] = useState('')
  const [officeAddressSame, setOfficeAddressSame] = useState(true)
  const [parishPhones, setParishPhones] = useState<string[]>([''])
  const [parishEmails, setParishEmails] = useState<string[]>([''])
  const [parishWebsite, setParishWebsite] = useState('')
  const [parishDiocese, setParishDiocese] = useState('')
  
  // Parish management (Primary Admin only)
  const [parishes, setParishes] = useState<ParishResponse[]>([])
  const [loadingParishes, setLoadingParishes] = useState(false)
  const [selectedParishId, setSelectedParishId] = useState<string | null>(null)
  const [isEditingParish, setIsEditingParish] = useState(false)
  const [parishDeleteConfirm, setParishDeleteConfirm] = useState('')
  const [showParishSetupModal, setShowParishSetupModal] = useState(false)
  
  // Password fields
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  
  // Emergency contacts (for parents)
  const [emergencyPhones, setEmergencyPhones] = useState<string[]>([''])
  const [parentProfileId, setParentProfileId] = useState<string | null>(null)
  
  // Join Parish (for admins not yet attached to a parish)
  
  // Subscription
  const [subscriptionStatus, setSubscriptionStatus] = useState<SubscriptionStatusResponse | null>(null)
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false)
  
  // Danger zone
  const [deleteConfirm, setDeleteConfirm] = useState('')
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // Load user and parish data
  useEffect(() => {
    const load = async () => {
      if (!user) return
      setLoading(true)
      
      // Load user details (don't block other loading on this)
      try {
        const userDetails = await usersApi.get(user.id)
        setFirstName(userDetails.first_name || '')
        setLastName(userDetails.last_name || '')
        setEmail(userDetails.email || '')
        setRole(userDetails.role_description || '')
        setTitle(userDetails.title || '')
        setCellPhone(userDetails.cell_phone || '')
        setAddress(userDetails.address || '')
        setCity(userDetails.city || '')
        setState(userDetails.state || '')
        setZipCode(userDetails.zip_code || '')
        
        // Auto-set title based on role
        const currentRole = userDetails.role_description || ''
        if (currentRole === 'Priest' && !userDetails.title) {
          setTitle('Father')
        } else if (currentRole === 'Deacon' && !userDetails.title) {
          setTitle('Deacon')
        }
        
          // Store user details
          setUserDetails(userDetails)
      } catch (error) {
        console.error('Failed to load user details:', error)
        // Don't block - use data from auth store
        if (user) {
          setFirstName(user.first_name || '')
          setLastName(user.last_name || '')
          setEmail(user.email || '')
        }
      }
      
      // Load all parishes for primary admin, or current parish for others (independent of user details)
      if (user.role === 'PRIMARY_ADMIN') {
        setLoadingParishes(true)
        try {
          const allParishes = await parishesApi.list()
          console.log('Settings: Loaded parishes from API:', allParishes)
          console.log('Settings: Parishes array length:', allParishes?.length)
          if (!Array.isArray(allParishes)) {
            console.error('Settings: API did not return an array:', allParishes)
            addToast({ message: 'Invalid response from server', type: 'error' })
            setParishes([])
          } else {
            // Use the same structure as ParishSelector component
            setParishes(allParishes)
            console.log('Settings: Set parishes state to:', allParishes)
          }
          
          // Set selected parish to current user's parish, or first parish if available
          const currentParishId = user.parish_id || (allParishes.length > 0 ? allParishes[0].id : null)
          setSelectedParishId(currentParishId)
          
          // Load selected parish data
          if (currentParishId) {
            const parish = await parishesApi.get(currentParishId)
            setParishName(parish.name || '')
            setParishAddress(parish.address || '')
            setParishCity(parish.city || '')
            setParishState(parish.state || '')
            setParishZip(parish.zip_code || '')
            setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
            setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
            setParishWebsite(parish.website || '')
            setParishDiocese(parish.diocese || '')
          }
        } catch (error: any) {
          console.error('Failed to load parishes:', error)
          addToast({ 
            message: `Failed to load parishes: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`, 
            type: 'error' 
          })
        } finally {
          setLoadingParishes(false)
        }
      } else if (user.parish_id) {
        // For non-primary admins, just load their parish (view-only)
            try {
              const parish = await parishesApi.get(user.parish_id)
            setParishName(parish.name || '')
            setParishAddress(parish.address || '')
            setParishCity(parish.city || '')
            setParishState(parish.state || '')
            setParishZip(parish.zip_code || '')
            setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
            setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
            setParishWebsite(parish.website || '')
            setParishDiocese(parish.diocese || '')
        } catch (error) {
          console.error('Failed to load parish:', error)
        }
        }
        
        // Load subscription status
        try {
          const status = await subscriptionsApi.status()
          setSubscriptionStatus(status)
        } catch {}
        
        // Load emergency contacts if parent
        if (user.role === 'PARENT') {
          try {
            // Try to get parent profile - may need backend /me endpoint
            const parentProfile = await parentsApi.getMyProfile()
            setParentProfileId(parentProfile.id)
            setEmergencyPhones(parentProfile.emergency_phone_numbers?.length ? [...parentProfile.emergency_phone_numbers] : [''])
          } catch (error) {
            console.error('Failed to load parent profile (backend endpoint may not exist yet):', error)
            // Initialize empty - will work once backend adds /me endpoint
            setEmergencyPhones([''])
          }
        }
      
        setLoading(false)
    }
    load()
  }, [user])

  // Auto-update title when role changes
  useEffect(() => {
    if (role === 'Priest' && title !== 'Father') {
      setTitle('Father')
    } else if (role === 'Deacon' && title !== 'Deacon') {
      setTitle('Deacon')
    }
  }, [role])

  const submitProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    setSaving(true)
    try {
      await usersApi.update(user.id, {
        first_name: firstName,
        last_name: lastName,
        title: title || undefined,
        role_description: role || undefined,
        cell_phone: cellPhone || undefined,
        address: address || undefined,
        city: city || undefined,
        state: state || undefined,
        zip_code: zipCode || undefined,
      })
      // Update auth store
      const updated = await authApi.getMe()
      updateUser(updated)
      addToast({ message: 'Profile updated successfully', type: 'success' })
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update profile',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const submitPassword = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    
    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      addToast({ message: 'Please fill in all password fields', type: 'error' })
      return
    }
    
    if (newPassword !== confirmPassword) {
      addToast({ message: 'New passwords do not match', type: 'error' })
      return
    }
    
    // Check minimum length (8 for parents, 10 for others)
    const minLength = user.role === 'PARENT' ? 8 : 10
    if (newPassword.length < minLength) {
      addToast({ 
        message: `Password must be at least ${minLength} characters`, 
        type: 'error' 
      })
      return
    }
    
    setSaving(true)
    try {
      await usersApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      })
      addToast({ message: 'Password changed successfully', type: 'success' })
      // Clear form
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update password',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  // Load parish data when selection changes (Primary Admin only)
  useEffect(() => {
    const loadParishData = async () => {
      if (user?.role !== 'PRIMARY_ADMIN' || !selectedParishId) return
      
      try {
        const parish = await parishesApi.get(selectedParishId)
        setParishName(parish.name || '')
        setParishAddress(parish.address || '')
        setParishCity(parish.city || '')
        setParishState(parish.state || '')
        setParishZip(parish.zip_code || '')
        setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
        setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
        setParishWebsite(parish.website || '')
        setParishDiocese(parish.diocese || '')
        setIsEditingParish(false) // Reset editing mode when switching parishes
        setParishDeleteConfirm('') // Clear delete confirmation
      } catch (error) {
        console.error('Failed to load parish data:', error)
      }
    }
    
    loadParishData()
  }, [selectedParishId, user?.role])

  const submitParish = async (e: React.FormEvent) => {
    e.preventDefault()
    const parishId = user?.role === 'PRIMARY_ADMIN' ? selectedParishId : user?.parish_id
    if (!parishId) return
    setSaving(true)
    try {
      await parishesApi.update(parishId, {
        name: parishName,
        address: parishAddress,
        city: parishCity,
        state: parishState,
        zip_code: parishZip,
        phone_numbers: parishPhones.filter(p => p.trim()),
        email_addresses: parishEmails.filter(e => e.trim()),
        website: parishWebsite || undefined,
        diocese: parishDiocese || undefined,
      })
      addToast({ message: 'Parish settings updated successfully', type: 'success' })
      setIsEditingParish(false)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to update parish settings',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteParish = async () => {
    if (user?.role !== 'PRIMARY_ADMIN' || !selectedParishId) return
    
    if (parishDeleteConfirm !== 'delete') {
      addToast({ message: 'Please type "delete" to confirm', type: 'error' })
      return
    }
    
    if (!confirm('Are you absolutely sure? This will delete this parish. The data will be retained in the backend for 90 days. This action cannot be undone.')) {
      return
    }
    
    setSaving(true)
    try {
      await parishesApi.delete(selectedParishId)
      addToast({
        message: 'Parish deleted successfully. Data retained for 90 days.',
        type: 'success',
      })
      
      // Reload parishes list (same as header selector)
      setLoadingParishes(true)
      try {
        const allParishes = await parishesApi.list()
        console.log('After delete: Loaded parishes from API:', allParishes)
        setParishes(allParishes)
        
        // Select first available parish, or clear if none
        if (allParishes.length > 0) {
          const newSelectedId = allParishes[0].id
          setSelectedParishId(newSelectedId)
          // Load the new selected parish data
          const parish = await parishesApi.get(newSelectedId)
          setParishName(parish.name || '')
          setParishAddress(parish.address || '')
          setParishCity(parish.city || '')
          setParishState(parish.state || '')
          setParishZip(parish.zip_code || '')
          setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
          setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
          setParishWebsite(parish.website || '')
          setParishDiocese(parish.diocese || '')
        } else {
          setSelectedParishId(null)
          // Clear form
          setParishName('')
          setParishAddress('')
          setParishCity('')
          setParishState('')
          setParishZip('')
          setParishPhones([''])
          setParishEmails([''])
          setParishWebsite('')
          setParishDiocese('')
        }
      } catch (error: any) {
        console.error('Failed to reload parishes after delete:', error)
        addToast({ 
          message: `Failed to reload parishes: ${error?.response?.data?.detail || error?.message || 'Unknown error'}`, 
          type: 'error' 
        })
      } finally {
        setLoadingParishes(false)
      }
      
      setParishDeleteConfirm('')
      setIsEditingParish(false)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to delete parish',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteEverything = async () => {
    if (deleteConfirm !== 'delete') {
      addToast({ message: 'Please type "delete" to confirm', type: 'error' })
      return
    }
    
    if (!user?.parish_id) return
    
    if (!confirm('Are you absolutely sure? This will delete ALL parish data. This action cannot be undone.')) {
      return
    }
    
    setSaving(true)
    try {
      await parishesApi.delete(user.parish_id)
      addToast({
        message: 'Parish data deleted. You will be logged out.',
        type: 'success',
      })
      // Clear auth state, then redirect to login.
      setTimeout(() => {
        logout()
        window.location.href = '/login'
      }, 2000)
    } catch (error: any) {
      addToast({
        message: error.response?.data?.detail || 'Failed to delete parish data',
        type: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner label="Loading settings..." />
      </div>
    )
  }

  const isPrimaryAdmin = user.role === 'PRIMARY_ADMIN'

  return (
    <div className="min-h-screen bg-gray-50 page-transition">
      <AppHeader />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Settings & Details</h1>
        
        {isPrimaryAdmin && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 rounded-xl border border-yellow-200 bg-yellow-50 text-yellow-800"
          >
            <p className="font-semibold">You are the Primary Administrator of this Subscription</p>
            <p className="text-sm mt-1">Account billing and subscription is managed <em>only</em> from your account.</p>
          </motion.div>
        )}

        <div className="grid grid-cols-1 gap-6">
          {/* Profile Section */}
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22 }}
            className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
          >
            <h2 className="text-lg font-semibold mb-3">Your Profile</h2>
            <form onSubmit={submitProfile} className="space-y-3">
              <div className="text-sm text-gray-600 mb-2">
                Account number: <span className="font-mono">{userDetails?.account_number || 'N/A'}</span>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Email *</label>
                <input
                  type="email"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled
                  title="Email cannot be changed"
                />
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">First Name *</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Last Name *</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {isAdminRole(user.role) && (<div>
                  <label className="block text-sm font-medium mb-1">Role (Optional)</label>
                  <select
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    <option value="">Select role...</option>
                    {ROLE_OPTIONS.map(r => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>)}
                <div>
                  <label className="block text-sm font-medium mb-1">Title</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="e.g., Father, Mrs."
                    disabled={role === 'Priest' || role === 'Deacon'}
                    title={role === 'Priest' || role === 'Deacon' ? 'Title auto-set based on role' : ''}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Cell Phone</label>
                <input
                  type="tel"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={cellPhone}
                  onChange={(e) => setCellPhone(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Address</label>
                <input
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Street address"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">City</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">State</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ZIP</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={zipCode}
                    onChange={(e) => setZipCode(e.target.value)}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  disabled={saving}
                  title="Save all changes to your personal profile including name, role, title, and contact information"
                >
                  {saving ? 'Saving...' : 'Save Profile'}
                </button>
              </div>
            </form>
          </motion.section>

          {/* Password Section — not for teachers: their Admin sets and resets it */}
          {user.role === 'TEACHER' ? (
            <section className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm">
              <h2 className="text-lg font-semibold mb-1">Password</h2>
              <p className="text-sm text-gray-600">Your password is managed by your parish admin. Ask them if you need it reset.</p>
            </section>
          ) : (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.05 }}
            className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
          >
            <h2 className="text-lg font-semibold mb-3">Password</h2>
            <form onSubmit={submitPassword} className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Current Password</label>
                <input
                  type="password"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <input
                  type="password"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder={`Enter new password (min ${user?.role === 'PARENT' ? 8 : 10} characters)`}
                  minLength={user?.role === 'PARENT' ? 8 : 10}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Confirm New Password</label>
                <input
                  type="password"
                  className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  minLength={user?.role === 'PARENT' ? 8 : 10}
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="submit"
                  className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                  disabled={saving}
                  title="Change your password. New password must be at least 8 characters for parents, 10 for admins/teachers."
                >
                  {saving ? 'Updating...' : 'Update Password'}
                </button>
              </div>
            </form>
          </motion.section>
          )}


          {/* Emergency Contacts Section (Parents only) */}
          {user.role === 'PARENT' && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.075 }}
              className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
            >
              <h2 className="text-lg font-semibold mb-3">Emergency Contact Phone Numbers</h2>
              <form
                onSubmit={async (e) => {
                  e.preventDefault()
                  if (!parentProfileId) {
                    addToast({ message: 'Unable to update emergency contacts. Please refresh and try again.', type: 'error' })
                    return
                  }
                  setSaving(true)
                  try {
                    await parentsApi.updateMyProfile({
                      emergency_phone_numbers: emergencyPhones.filter(p => p.trim()),
                    })
                    addToast({ message: 'Emergency contacts updated successfully', type: 'success' })
                  } catch (error: any) {
                    addToast({
                      message: error.response?.data?.detail || 'Failed to update emergency contacts. Backend endpoint may not be available yet.',
                      type: 'error',
                    })
                  } finally {
                    setSaving(false)
                  }
                }}
                className="space-y-3"
              >
                <div className="text-sm text-gray-600 mb-3">
                  Add up to 10 additional emergency contact phone numbers (not including your cell phone).
                </div>
                <div className="space-y-2">
                  {emergencyPhones.map((phone, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input
                        type="tel"
                        className="input-field flex-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        placeholder="Phone number"
                        value={phone}
                        onChange={(e) => {
                          const updated = [...emergencyPhones]
                          updated[i] = e.target.value
                          setEmergencyPhones(updated)
                        }}
                        maxLength={20}
                      />
                      {emergencyPhones.length > 1 && (
                        <button
                          type="button"
                          className="btn-secondary text-sm px-3 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                          onClick={() => {
                            setEmergencyPhones(prev => prev.filter((_, idx) => idx !== i))
                          }}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {emergencyPhones.length < 10 && (
                  <button
                    type="button"
                    className="btn-secondary text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    onClick={() => {
                      setEmergencyPhones(prev => [...prev, ''])
                    }}
                  >
                    + Add Emergency Contact
                  </button>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="submit"
                    className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    disabled={saving}
                    title="Save up to 10 emergency contact phone numbers for your children"
                  >
                    {saving ? 'Saving...' : 'Save Emergency Contacts'}
                  </button>
                </div>
              </form>
            </motion.section>
          )}

          {/* Parish Settings Section — admins only (spec: Admin "Settings & Details"; editable by Primary/Designated) */}
          {isAdminRole(user.role) && (isPrimaryAdmin || user.parish_id) && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.1 }}
              className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
            >
              <h2 className="text-lg font-semibold mb-3">Parish Details</h2>
              
              {/* Parish Selector (Primary Admin only) */}
              {isPrimaryAdmin && (
                <div className="mb-4 pb-4 border-b border-gray-200">
                  <label className="block text-sm font-medium mb-2">Select Parish</label>
                  <select
                    className="input-field max-w-md focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={selectedParishId || ''}
                    onChange={(e) => {
                      const value = e.target.value
                      if (value === '__create__') {
                        setShowParishSetupModal(true)
                        return
                      }
                      setSelectedParishId(value || null)
                    }}
                    disabled={saving || loadingParishes}
                  >
                    <option value="">-- Select a parish --</option>
                    {loadingParishes ? (
                      <option value="" disabled>Loading parishes...</option>
                    ) : parishes.length === 0 ? (
                      <option value="" disabled>No parishes available</option>
                    ) : (
                      parishes.map(parish => (
                        <option key={parish.id} value={parish.id}>{parish.name}</option>
                      ))
                    )}
                    {isPrimaryAdmin && <option value="__create__">+ Create New Parish…</option>}
                  </select>
                  {loadingParishes && (
                    <p className="text-xs text-gray-500 mt-1">Loading parishes...</p>
                  )}
                  {!loadingParishes && parishes.length === 0 && (
                    <p className="text-xs text-gray-500 mt-1">No parishes found. Select "+ Create New Parish…" above to create one.</p>
                  )}
                </div>
              )}
              
              {/* Action Buttons (Primary Admin only) */}
              {isPrimaryAdmin && selectedParishId && (
                <div className="mb-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingParish(!isEditingParish)
                      setParishDeleteConfirm('')
                    }}
                    className={`btn-secondary flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-yellow-400/60 ${isEditingParish ? 'bg-yellow-100 border-yellow-400' : ''}`}
                    disabled={saving}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {isEditingParish ? 'Cancel Editing' : 'Edit Parish Details'}
                  </button>
                  
                  <button
                    type="button"
                    onClick={handleDeleteParish}
                    className="btn-secondary bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    disabled={saving || parishDeleteConfirm !== 'delete'}
                    title="Delete this parish. Data will be retained for 90 days."
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete Parish
                  </button>
                </div>
              )}
              
              {/* Delete Confirmation (Primary Admin only) */}
              {isPrimaryAdmin && selectedParishId && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <label className="block text-sm font-medium mb-1 text-red-800">
                    Type "delete" to confirm deletion:
                  </label>
                  <input
                    type="text"
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-red-400"
                    value={parishDeleteConfirm}
                    onChange={(e) => setParishDeleteConfirm(e.target.value)}
                    placeholder="Type 'delete' to confirm"
                    disabled={saving}
                  />
                  <p className="text-xs text-red-600 mt-1">
                    Note: Parish data will be retained in the backend for 90 days after deletion.
                  </p>
                </div>
              )}
              
              <form onSubmit={submitParish} className="space-y-3">
                {(!isPrimaryAdmin || selectedParishId) ? (
                  <>
                <div>
                  <label className="block text-sm font-medium mb-1">Parish Name *</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={parishName}
                    onChange={(e) => setParishName(e.target.value)}
                    readOnly={!isEditingParish || !isPrimaryAdmin}
                    disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Parish Address</label>
                  <input
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    value={parishAddress}
                    onChange={(e) => setParishAddress(e.target.value)}
                    placeholder="Street address"
                    readOnly={!isEditingParish || !isPrimaryAdmin}
                    disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">City</label>
                    <input
                      className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      value={parishCity}
                      onChange={(e) => setParishCity(e.target.value)}
                      readOnly={!isEditingParish || !isPrimaryAdmin}
                      disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">State</label>
                    <input
                      className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      value={parishState}
                      onChange={(e) => setParishState(e.target.value)}
                      readOnly={!isEditingParish || !isPrimaryAdmin}
                      disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">ZIP</label>
                    <input
                      className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      value={parishZip}
                      onChange={(e) => setParishZip(e.target.value)}
                      readOnly={!isEditingParish || !isPrimaryAdmin}
                      disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    />
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="officeSame"
                    checked={officeAddressSame}
                    onChange={(e) => setOfficeAddressSame(e.target.checked)}
                    className="focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                  />
                  <label htmlFor="officeSame" className="text-sm">Parish office address same as above</label>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Parish Phone Numbers</label>
                  {parishPhones.map((phone, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="tel"
                        className="input-field flex-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        value={phone}
                        onChange={(e) => {
                          const updated = [...parishPhones]
                          updated[idx] = e.target.value
                          setParishPhones(updated)
                        }}
                        placeholder="Phone number"
                        readOnly={!isEditingParish}
                        disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                      />
                      {isEditingParish && idx === parishPhones.length - 1 && parishPhones.length < 5 && (
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => setParishPhones([...parishPhones, ''])}
                          disabled={saving}
                        >
                          + Add
                        </button>
                      )}
                      {isEditingParish && parishPhones.length > 1 && (
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => setParishPhones(parishPhones.filter((_, i) => i !== idx))}
                          disabled={saving}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Parish Email Contacts</label>
                  {parishEmails.map((email, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input
                        type="email"
                        className="input-field flex-1 focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                        value={email}
                        onChange={(e) => {
                          const updated = [...parishEmails]
                          updated[idx] = e.target.value
                          setParishEmails(updated)
                        }}
                        placeholder="Email address"
                        readOnly={!isEditingParish}
                        disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                      />
                      {isEditingParish && idx === parishEmails.length - 1 && parishEmails.length < 5 && (
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => setParishEmails([...parishEmails, ''])}
                          disabled={saving}
                        >
                          + Add
                        </button>
                      )}
                      {isEditingParish && parishEmails.length > 1 && (
                        <button
                          type="button"
                          className="btn-secondary text-xs"
                          onClick={() => setParishEmails(parishEmails.filter((_, i) => i !== idx))}
                          disabled={saving}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Website</label>
                    <input
                      type="url"
                      className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      value={parishWebsite}
                      onChange={(e) => setParishWebsite(e.target.value)}
                      placeholder="https://..."
                      readOnly={!isEditingParish || !isPrimaryAdmin}
                      disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Diocese</label>
                    <input
                      className="input-field w-full focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                      value={parishDiocese}
                      onChange={(e) => setParishDiocese(e.target.value)}
                      readOnly={!isEditingParish || !isPrimaryAdmin}
                      disabled={(!isEditingParish && isPrimaryAdmin) || (!isPrimaryAdmin) || saving}
                    />
                  </div>
                </div>
                    {isEditingParish && (
                <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="button"
                          onClick={() => {
                            setIsEditingParish(false)
                            // Reload parish data to reset changes
                            if (selectedParishId) {
                              parishesApi.get(selectedParishId).then(parish => {
                                setParishName(parish.name || '')
                                setParishAddress(parish.address || '')
                                setParishCity(parish.city || '')
                                setParishState(parish.state || '')
                                setParishZip(parish.zip_code || '')
                                setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
                                setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
                                setParishWebsite(parish.website || '')
                                setParishDiocese(parish.diocese || '')
                              })
                            }
                          }}
                          className="btn-secondary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                          disabled={saving}
                        >
                          Cancel
                        </button>
                  <button
                    type="submit"
                    className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    disabled={saving}
                    title="Save all parish details including name, address, contact information, and diocese"
                  >
                    {saving ? 'Saving...' : 'Save Parish Settings'}
                  </button>
                </div>
                    )}
                    {!isEditingParish && !isPrimaryAdmin && (
                      <div className="flex justify-end gap-2 pt-2">
                        <button
                          type="submit"
                          className="btn-primary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                          disabled={saving}
                          title="Save all parish details including name, address, contact information, and diocese"
                        >
                          {saving ? 'Saving...' : 'Save Parish Settings'}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>Please select a parish from the dropdown above to view or edit its details.</p>
                  </div>
                )}
              </form>
            </motion.section>
          )}

          {/* Subscription/Billing Section */}
          {isPrimaryAdmin && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.15 }}
              className="p-5 rounded-xl border border-gray-200 bg-white shadow-sm hover-raise"
            >
              <h2 className="text-lg font-semibold mb-3">Billing Details</h2>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Subscription Status</label>
                  <div className="text-lg font-semibold text-green-600 capitalize">
                    {subscriptionStatus?.status || 'Active'}
                  </div>
                </div>
                {subscriptionStatus?.current_period_end && (
                  <div>
                    <label className="block text-sm font-medium mb-1">Next Billing Cycle</label>
                    <div className="text-sm text-gray-700">
                      {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
                    </div>
                    {subscriptionStatus.days_until_expiry !== null && subscriptionStatus.days_until_expiry !== undefined && (
                      <div className="text-xs text-gray-500 mt-1">
                        {subscriptionStatus.days_until_expiry} days until expiration
                      </div>
                    )}
                  </div>
                )}
                <div className="text-sm text-gray-600 mt-4">
                  <p>Annual subscription: {SUBSCRIPTION_CONFIG.ANNUAL_PRICE_FORMATTED}</p>
                  <p className="mt-1">You may cancel for a full refund within 30 days.</p>
                </div>
                <div className="mt-3">
                  <button
                    className="btn-secondary focus:outline-none focus:ring-2 focus:ring-yellow-400/60"
                    onClick={() => setShowSubscriptionModal(true)}
                  >
                    Manage Subscription
                  </button>
                </div>
              </div>
            </motion.section>
          )}

          {/* Danger Zone */}
          {isPrimaryAdmin && (
            <motion.section
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 260, damping: 22, delay: 0.2 }}
              className="p-5 rounded-xl border-2 border-red-200 bg-red-50"
            >
              <h2 className="text-lg font-semibold mb-3 text-red-800">DANGER ZONE</h2>
              <div className="space-y-3">
                <p className="text-sm text-red-700">
                  Delete EVERYTHING: This will permanently delete all parish data, including users, classes, students, and messages.
                </p>
                <p className="text-xs text-red-600">
                  Note: This information may be saved for up to 90 days after deletion.
                </p>
                <p className="text-xs text-red-600">
                  Also note: You can utilize previous-year's data in future years to jump-start class calendar creation - so we wouldn't recommend deleting if you plan to use this software in the future.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-1 text-red-800">
                    Type "delete" to confirm:
                  </label>
                  <input
                    type="text"
                    className="input-field w-full focus:outline-none focus:ring-2 focus:ring-red-400"
                    value={deleteConfirm}
                    onChange={(e) => setDeleteConfirm(e.target.value)}
                    placeholder="Type 'delete' to confirm"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleDeleteEverything}
                  disabled={deleteConfirm !== 'delete' || saving}
                  className="btn-secondary bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Permanently delete ALL parish data including users, classes, students, and messages. This action cannot be undone. Data may be saved for up to 90 days after deletion."
                >
                  {saving ? 'Deleting...' : 'Delete Everything'}
                </button>
              </div>
            </motion.section>
          )}
        </div>
      </main>
      <AppFooter />
      <SubscriptionManagementModal
        open={showSubscriptionModal}
        onClose={() => setShowSubscriptionModal(false)}
        subscriptionStatus={subscriptionStatus}
        onStatusUpdate={async () => {
          // Refresh subscription status
          try {
            const status = await subscriptionsApi.status()
            setSubscriptionStatus(status)
          } catch {}
        }}
      />
      <ParishSetupModal
        open={showParishSetupModal}
        onClose={() => setShowParishSetupModal(false)}
        onCreated={async (parishId) => {
          // Refresh user info
          try {
            const me = await authApi.getMe()
            updateUser(me)
            
            // Reload parishes list
            setLoadingParishes(true)
            try {
              const allParishes = await parishesApi.list()
              setParishes(allParishes)
              setSelectedParishId(parishId)
              
              // Load the new parish data
              const parish = await parishesApi.get(parishId)
              setParishName(parish.name || '')
              setParishAddress(parish.address || '')
              setParishCity(parish.city || '')
              setParishState(parish.state || '')
              setParishZip(parish.zip_code || '')
              setParishPhones(parish.phone_numbers?.length ? [...parish.phone_numbers] : [''])
              setParishEmails(parish.email_addresses?.length ? [...parish.email_addresses] : [''])
              setParishWebsite(parish.website || '')
              setParishDiocese(parish.diocese || '')
            } catch (error) {
              console.error('Failed to reload parishes after creation:', error)
            } finally {
              setLoadingParishes(false)
            }
          } catch (error) {
            console.error('Failed to refresh user info:', error)
          }
        }}
      />
    </div>
  )
}
